// The in-place help: a "?" beside a thing on a screen that opens the help
// center's words about it, where it is.
//
//   <HelpHint helpKey="vendor.sales.figures#views" />
//
// A help key names an article (what the article declares in its "Help keys",
// scripts/help-center publish.js or the CMS), and after "#" a section of it
// (the id of a heading in the article: <h2 id="views">). The panel shows that
// section — or the whole article — with a link to the article's page, in its
// own help center (a vendor's under /vendor/help-center). It opens on hover
// where there is a mouse, on a tap where there is not, and closes on a click
// outside or Escape.
//
// The keys of a page are asked for together (one request after the first
// hint renders, the rest ride along) and kept for the page's life; a key the
// help center has no article for renders nothing, so a screen may name keys
// before their articles are written.
import React, { useEffect, useRef, useState } from "react"
import parse from "html-react-parser"
import { QuestionMarkCircleIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import Popover from "aldoo-ra/Popover"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { updateCDNUrls } from "aldoo-ra/HtmlEditor/cdn-urls"
import { GetHelp } from "../api"

// key → answer, per language; and the keys waiting for the next request
const cache = new Map()
const listeners = new Map()
let pending = new Set()
let timer = null
let language = null

const languageNow = () => {
  try {
    return (localStorage.getItem("current-site-language") || "en").replace(/"/g, "")
  } catch {
    return "en"
  }
}

const cacheKey = (key, lang) => `${lang}:${key}`

// the article's address in the page's language: the site's /xx prefix (none for English),
// the same rule as aldoo-ra/Link — the API writes the address without it
const localizedUrl = (url, lang) => (url && lang && lang !== "en" && !/^\/[a-z]{2}(\/|$)/.test(url) ? `/${lang}${url}` : url)

const notify = (key) => {
  for (const listener of listeners.get(key) || []) listener()
}

async function flush() {
  timer = null
  const keys = [...pending]
  pending = new Set()
  if (!keys.length) return
  const lang = language
  try {
    const answer = await GetHelp(keys, lang)
    for (const key of keys) {
      cache.set(cacheKey(key, lang), answer?.help?.[key] || { key, found: false })
      notify(key)
    }
  } catch {
    // asked again on the next render that needs it
    for (const key of keys) {
      cache.delete(cacheKey(key, lang))
      notify(key)
    }
  }
}

function ask(key) {
  const lang = languageNow()
  if (language !== lang) {
    // the language changed: what was kept is of the old one
    language = lang
  }
  const id = cacheKey(key, lang)
  if (cache.has(id)) return
  cache.set(id, null) // asked, not answered
  pending.add(key)
  if (!timer) timer = setTimeout(flush, 30)
}

/** The help behind a key: `undefined` while loading, `{ found: false }` for none, else the article or its section. */
export function useHelp(helpKey) {
  const [, tick] = useState(0)
  useEffect(() => {
    if (!helpKey) return undefined
    const listener = () => tick((n) => n + 1)
    listeners.set(helpKey, [...(listeners.get(helpKey) || []), listener])
    ask(helpKey)
    return () => listeners.set(helpKey, (listeners.get(helpKey) || []).filter((l) => l !== listener))
  }, [helpKey])
  if (!helpKey) return { found: false }
  return cache.get(cacheKey(helpKey, languageNow())) || undefined
}

// the article's HTML, its images on the CDN, headings and lists readable at the panel's size
const PROSE =
  "text-[14px] leading-relaxed text-text [&_h1]:hidden [&_h2]:mt-0 [&_h2]:mb-2 [&_h2]:text-[15px] [&_h2]:font-bold [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-[14px] [&_h3]:font-bold [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline [&_figure]:my-3 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg"

/**
 * The "?" and its panel. `helpKey`: the article (and section) to show;
 * `label`: what the button is called for a screen reader (the article's title
 * when omitted); `placement` / `align`: where the panel opens (Popover);
 * `className`: the button's; `iconClassName`: the icon's size and colour.
 */
export default function HelpHint({ helpKey, label, placement = "bottom", align = "start", className = "", iconClassName = "", panelClassName = "" }) {
  const texts = TextBundle({ id: "help-center" })
  const help = useHelp(helpKey)
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const anchor = useRef(null)
  const opening = useRef(null)
  const closing = useRef(null)
  // a mouse opens it by resting on the "?": a touch screen has no hover and taps instead
  const hovers = typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches

  // the mouse rests on the "?" a moment before the panel opens (a pass over it opens
  // nothing), and the panel stays a moment once the mouse leaves, long enough to
  // reach it (the "?" and the panel share these timers, so crossing between them
  // never closes it)
  const OPEN_AFTER = 180
  const CLOSE_AFTER = 400
  const enter = () => {
    if (!hovers) return
    clearTimeout(closing.current)
    if (hover) return
    clearTimeout(opening.current)
    opening.current = setTimeout(() => setHover(true), OPEN_AFTER)
  }
  const leave = () => {
    if (!hovers) return
    clearTimeout(opening.current)
    clearTimeout(closing.current)
    closing.current = setTimeout(() => setHover(false), CLOSE_AFTER)
  }
  useEffect(() => () => { clearTimeout(opening.current); clearTimeout(closing.current) }, [])

  if (!help?.found) return null
  const shown = open || hover
  const title = help.title || ""
  const name = label || (help.section ? `${texts?.help || "Help"}: ${title}` : title)

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={name}
        aria-expanded={shown}
        title={name}
        data-testid="help-hint"
        data-help-key={helpKey}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onClick={(e) => {
          // on a tile that is itself a button, the "?" is its own click
          e.stopPropagation()
          e.preventDefault()
          setOpen((v) => !v)
        }}
        className={twMerge("inline-flex items-center justify-center rounded-full align-middle text-current opacity-60 hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity", className)}
      >
        <QuestionMarkCircleIcon className={twMerge("w-4 h-4", iconClassName)} aria-hidden="true" />
      </button>
      {/* no taller than most of the screen: a long article scrolls inside the panel (Popover keeps it clear of the top bars) */}
      <Popover open={shown} onClose={() => { setOpen(false); setHover(false) }} anchorRef={anchor} placement={placement} align={align} className={twMerge("w-[360px] max-w-[calc(100vw-16px)] p-4 text-left cursor-auto", panelClassName)} style={{ maxHeight: "min(70vh, 640px)" }}>
        <div onMouseEnter={enter} onMouseLeave={leave} onClick={(e) => e.stopPropagation()} data-testid="help-hint-panel">
          {help.section && <div className="text-xs font-semibold uppercase tracking-wide text-text_secondary mb-2">{title}</div>}
          <div className={PROSE}>{parse(updateCDNUrls(help.html || ""))}</div>
          <a href={localizedUrl(help.url, languageNow())} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline" data-testid="help-hint-link">
            {texts?.read_the_full_article || "Read the full article"}
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        </div>
      </Popover>
    </>
  )
}

export { HelpHint }
